import classNames from "classnames";
import { useEffect, useState } from "react";
import { Tabs } from "..";
import { NumberCard } from "../../../../components/cards/numberCard";
import { ProgressBar } from "../../../../components/progressBar";
import { GuestsTable } from "../../../../components/table/app_tables/guestsTable";
import { MiniHeader } from "../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import {
  CreatedEvent,
  GetEventGuestsResponse,
  GetEventTicketsResponse,
} from "../../../../redux/features/event/event.types";
import { useGetPurchaseStatisticsQuery } from "../../../../redux/features/payment/payment.api";
import { TicketSalesBreakdown } from "../../../../utils/types";

export const EventOverview = ({
  event,
  setTab,
  ticketBreakdown,
  ticketsData,
  guests,
  fetchingGuests = false,
}: {
  event: CreatedEvent;
  setTab: (tab: Tabs) => void;
  ticketBreakdown: TicketSalesBreakdown;
  ticketsData?: GetEventTicketsResponse;
  guests?: GetEventGuestsResponse;
  fetchingGuests?: boolean;
}) => {
  const [hasPaidTickets, setHasPaidTickets] = useState(false);

  const { data: statistics, isFetching: fetchingStatistics } =
    useGetPurchaseStatisticsQuery(
      {
        eventId: event._id,
      },
      { refetchOnMountOrArgChange: true, skip: !hasPaidTickets },
    );

  useEffect(() => {
    setHasPaidTickets(
      ticketsData?.data?.tickets?.map((t) => t.ticketType).includes("paid") ??
        false,
    );
  }, [ticketsData]);

  return (
    <>
      {hasPaidTickets && (
        <BgWrapper className="flex flex-col gap-3 !rounded-t-none">
          <div className="flex items-center justify-between gap-4">
            <MiniHeader header="Sales Overview" />

            <button
              type="button"
              className="text-sm font-bold text-grey-700 underline underline-offset-2 md:text-base"
              onClick={() => {
                setTab("tickets");
              }}
            >
              View All
            </button>
          </div>

          <div className="grid grid-cols-1 gap-[10px] md:grid-cols-3">
            <NumberCard
              value={
                fetchingStatistics ? 0 : (statistics?.data.totalAmount ?? 0)
              }
              text="Total Tickets Sold"
              isCurrency={false}
              tooltipId={"totalTicketsSold"}
              tooltipText={"The total number of tickets that have been bought."}
            />

            <NumberCard
              value={
                fetchingStatistics
                  ? 0
                  : (statistics?.data.totalSuccessfulPayments ?? 0)
              }
              text="Total Sales Revenue"
              tooltipId={"totalSalesRevenue"}
              tooltipText={
                "The total amount of tickets sold which includes our fees."
              }
            />

            <NumberCard
              value={
                fetchingStatistics
                  ? 0
                  : (statistics?.data.totalPendingPayments ?? 0)
              }
              text="Total Amount Made"
              tooltipId={"totalAmountMade"}
              tooltipText={
                "You'll receive this amount in your payout account—your earnings from ticket sales after our fees have been deducted."
              }
            />
          </div>
        </BgWrapper>
      )}

      <BgWrapper
        className={classNames("flex flex-col gap-3", {
          "!rounded-t-none": !hasPaidTickets,
        })}
      >
        <MiniHeader header="Guests" />

        <p className="text-2xl font-medium tracking-[-0.04em] text-grey-600">
          {guests?.data?.totalCount ?? 0} guests
        </p>

        <ProgressBar
          step={ticketBreakdown.map((tb) => tb.purchaseQuantity)}
          className="!h-[8px]"
          colors={ticketBreakdown.map((tb) => tb.color.split(",")[0])}
        />

        <div className="mt-3 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <MiniHeader header="Recent guests" />

            <button
              type="button"
              className="text-sm font-bold text-grey-700 underline underline-offset-2 md:text-base"
              onClick={() => {
                setTab("guests");
              }}
            >
              View All
            </button>
          </div>

          <GuestsTable
            guests={guests?.data?.data ?? []}
            loading={fetchingGuests}
            showTimeAgo={false}
            ticketColor={ticketBreakdown.map((tb) => {
              return {
                ticketId: tb.ticketId,
                color: tb.color,
              };
            })}
          />
        </div>
      </BgWrapper>
    </>
  );
};
